/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import ITheme from "@atg/icf-react-components/themes/Theme";
import clsx from "clsx";
import { FC } from "react";
import { createUseStyles } from "react-jss";

import {
  FontSize,
  JSS_FONT_FAMILY_SECONDARY,
  JSS_MEDIA_DESKTOP_MIN,
  JSS_MEDIA_MOBILE_MAX,
  JSS_MEDIA_TABLET_MAX,
  JSS_MEDIA_TABLET_MIN,
} from "~/constants";
import useOnLanding from "~/hooks/useOnLanding";
import { ComponentBaseProps } from "~/types";

interface StyleProps {
  active: boolean;
  alternativeColor: boolean;
  onLanding: boolean;
}

const useStyles = createUseStyles<string, StyleProps, ITheme>((theme) => ({
  navItemText: {
    "&, & > *": {
      border: "none",
      background: "none",
      cursor: "pointer",
      padding: 0,
      margin: 0,
      fontSize: FontSize.DEFAULT,
      letterSpacing: 0.44,
      fontWeight: 300,
      fontFamily: JSS_FONT_FAMILY_SECONDARY,
      transition: "color 0.3s ease-in-out",
      "&:hover": {
        color: theme.colors.primary,
      },
      [JSS_MEDIA_MOBILE_MAX]: {
        color: theme.colors.baseContrast100,
      },
      [JSS_MEDIA_TABLET_MAX]: {
        color: (props) => (props.onLanding ? theme.colors.baseContrast100 : ""),
      },
      [JSS_MEDIA_TABLET_MIN]: {
        color: (props) => (props.alternativeColor ? theme.colors.baseContrast020 : theme.colors.baseContrast100),
      },
      [JSS_MEDIA_DESKTOP_MIN]: {
        color: (props) => {
          if (props.onLanding) {
            return props.alternativeColor ? theme.colors.baseContrast085 : theme.colors.baseContrast100;
          } else {
            return "";
          }
        },
      },
    },
    [JSS_MEDIA_MOBILE_MAX]: {
      padding: "12px",
      display: "inline-block",
      minWidth: "200px",
    },
    [JSS_MEDIA_TABLET_MAX]: {
      padding: (props) => (props.onLanding ? "12px" : ""),
      display: (props) => (props.onLanding ? "inline-block" : ""),
      minWidth: (props) => (props.onLanding ? "200px" : ""),
    },
  },

  onLandingPage: {},
  onOtherPage: {},
  navItemTextActive: {
    "&, & > *": {
      cursor: "default",
      fontWeight: 500,
    },
    "&:hover": {
      color: theme.colors.baseContrast100,
    },
  },
}));

interface Props extends ComponentBaseProps {
  active?: boolean;
  alternativeColor?: boolean;
}

const NavItemText: FC<Props> = ({ active = false, alternativeColor = false, children }) => {
  const onLanding = useOnLanding();

  const classes = useStyles({ active, alternativeColor, onLanding });

  return <span className={clsx(classes.navItemText, { [classes.navItemTextActive]: active })}>{children}</span>;
};

export default NavItemText;
